/**
 * session-recorder/sessionStorageService.ts
 * 
 * Serviço de persistência, indexação e sincronização de sessões de telemetria
 * no Local IndexedDB, Cache Local e Firebase Storage.
 */

import type { RecordedSession, UserLocation, TesterDeviceInfo } from './types';

const STORAGE_KEY_DEVICE_ID = 'ac_tester_device_id';
const STORAGE_KEY_SESSIONS = 'ac_recorded_sessions_v1';
const STORAGE_KEY_ENABLED = 'ac_session_recorder_enabled';

/** Gera ou recupera o identificador persistente do dispositivo do testador */
export function getOrCreateTesterDeviceId(): string {
  try {
    let deviceId = localStorage.getItem(STORAGE_KEY_DEVICE_ID);
    if (!deviceId) {
      const rand = Math.random().toString(36).substring(2, 10);
      const time = Date.now().toString(36);
      deviceId = `dev_${time}_${rand}`;
      localStorage.setItem(STORAGE_KEY_DEVICE_ID, deviceId);
    }
    return deviceId;
  } catch {
    return 'dev_anonymous_' + Date.now();
  }
}

/** Verifica se o gravador de sessões está ativado */
export function isSessionRecorderEnabled(): boolean {
  try {
    const val = localStorage.getItem(STORAGE_KEY_ENABLED);
    // Habilitado por padrão para testes de homologação, mas pode ser desativado
    return val !== 'false';
  } catch {
    return true;
  }
}

/** Liga ou desliga o gravador de sessões (Master Switch) */
export function setSessionRecorderEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY_ENABLED, enabled ? 'true' : 'false');
  } catch (e) {
    console.warn('[SessionRecorder] Erro ao salvar estado do switch:', e);
  }
}

/** Extrai informações do dispositivo e viewport */
export function getTesterDeviceInfo(): TesterDeviceInfo {
  const ua = navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) || window.innerWidth < 768;
  const isTablet = /iPad|Tablet/i.test(ua) || (window.innerWidth >= 768 && window.innerWidth <= 1024);

  let browser = 'Chrome/Edge';
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Edg')) browser = 'Edge';

  let os = 'Windows';
  if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  else if (ua.includes('Macintosh')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';

  return {
    deviceId: getOrCreateTesterDeviceId(),
    userAgent: ua,
    browser,
    os,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    deviceType: isMobile ? 'mobile' : (isTablet ? 'tablet' : 'desktop'),
  };
}

/** Obtém geolocalização do testador com cache local e fallback gracioso */
export async function getTesterLocation(): Promise<UserLocation | null> {
  // 1. Tenta recuperar do cache de sessão recente (válido por 1 hora)
  const cachedLocStr = sessionStorage.getItem('ac_tester_location_cache');
  if (cachedLocStr) {
    try {
      const cached = JSON.parse(cachedLocStr);
      if (Date.now() - cached.timestamp < 3600000) {
        return cached;
      }
    } catch {}
  }

  // 2. Tenta obter coordenadas GPS nativas
  if ('geolocation' in navigator) {
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 4000,
          maximumAge: 300000,
          enableHighAccuracy: false,
        });
      });

      const loc: UserLocation = {
        latitude: Number(pos.coords.latitude.toFixed(5)),
        longitude: Number(pos.coords.longitude.toFixed(5)),
        accuracy: Math.round(pos.coords.accuracy),
        source: 'gps',
        timestamp: Date.now(),
      };

      try {
        sessionStorage.setItem('ac_tester_location_cache', JSON.stringify(loc));
      } catch {}

      return loc;
    } catch (err) {
      // Se permissão negada ou timeout, segue para fallback
    }
  }

  // 3. Fallback rápido via IP público se GPS indisponível
  try {
    const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(2500) });
    if (res.ok) {
      const data = await res.json();
      if (data.latitude && data.longitude) {
        const ipLoc: UserLocation = {
          latitude: data.latitude,
          longitude: data.longitude,
          city: data.city,
          region: data.region,
          country: data.country_name,
          source: 'ip_fallback',
          timestamp: Date.now(),
        };
        try {
          sessionStorage.setItem('ac_tester_location_cache', JSON.stringify(ipLoc));
        } catch {}
        return ipLoc;
      }
    }
  } catch {}

  return null;
}

/** Salva uma sessão gravada localmente com limite de histórico */
export function saveRecordedSessionLocally(session: RecordedSession): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SESSIONS);
    let sessions: RecordedSession[] = raw ? JSON.parse(raw) : [];

    // Substitui se já existir (atualização) ou insere no topo
    const existingIndex = sessions.findIndex((s) => s.id === session.id);
    if (existingIndex >= 0) {
      sessions[existingIndex] = session;
    } else {
      sessions.unshift(session);
    }

    // Mantém as últimas 30 sessões no localStorage para não estourar quota (5MB)
    if (sessions.length > 30) {
      sessions = sessions.slice(0, 30);
    }

    localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(sessions));
  } catch (e) {
    console.warn('[SessionRecorder] Erro ao salvar sessão no localStorage:', e);
  }
}

/** Carrega todas as sessões gravadas localmente */
export function loadAllRecordedSessions(): RecordedSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SESSIONS);
    if (!raw) return [];
    const parsed: RecordedSession[] = JSON.parse(raw);
    return parsed.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  } catch {
    return [];
  }
}

/** Exclui uma sessão gravada */
export function deleteRecordedSession(sessionId: string): void {
  try {
    const sessions = loadAllRecordedSessions().filter((s) => s.id !== sessionId);
    localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(sessions));
  } catch (e) {
    console.warn('[SessionRecorder] Erro ao deletar sessão:', e);
  }
}

/** Limpa todas as sessões gravadas */
export function clearAllRecordedSessions(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_SESSIONS);
  } catch {}
}

/** Exporta uma sessão individual como arquivo JSON para auditoria */
export function exportSessionAsJson(session: RecordedSession): void {
  const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(session, null, 2));
  const downloadAnchor = document.createElement('a');
  downloadAnchor.setAttribute('href', dataStr);
  downloadAnchor.setAttribute('download', `auditoria_sessao_${session.id}_${session.appVersion.version}.json`);
  document.body.appendChild(downloadAnchor);
  downloadAnchor.click();
  downloadAnchor.remove();
}
